// useSelection — a plain multi-select for the gallery (tick items, download them as
// a zip). Sibling to useMosaicSelection, but UNORDERED and UNCAPPED: a zip doesn't
// care about order or count, so this is just a Set of keys with toggle/clear. The
// two select modes (Mosaic vs Select) are mutually exclusive — the grid only ever
// activates one at a time. Pure React state, no cores import.

import { useCallback, useMemo, useState } from "react";
import type { MediaItem } from "./api";

export interface Selection {
  /** The selected keys (no meaningful order). */
  readonly keys: string[];
  /** Is select mode active? (the gallery shows tick badges + the zip bar) */
  readonly active: boolean;
  /** The last key the owner clicked — the anchor for a shift-click range. */
  readonly anchor: string | null;
  /** Enter select mode. */
  begin(): void;
  /** Leave select mode and clear the selection. */
  cancel(): void;
  /** Is `key` selected? */
  has(key: string): boolean;
  /** Toggle a key in/out of the selection; sets it as the range anchor. */
  toggle(key: string): void;
  /** Union-add a run of keys (a shift-click range) and set `anchor` to `anchor`. */
  addRange(keys: string[], anchor: string): void;
  /** Replace the selection with exactly `keys` (Select all over the loaded items). */
  setAll(keys: string[]): void;
  /** Deselect everything but stay in select mode. */
  clear(): void;
  /** Resolve the selection to the matching MediaItems, in the given list's order. */
  resolve(items: MediaItem[]): MediaItem[];
}

export function useSelection(): Selection {
  const [active, setActive] = useState(false);
  const [keys, setKeys] = useState<Set<string>>(() => new Set());
  const [anchor, setAnchor] = useState<string | null>(null);

  const begin = useCallback(() => setActive(true), []);
  const cancel = useCallback(() => {
    setActive(false);
    setKeys(new Set());
    setAnchor(null);
  }, []);

  const has = useCallback((key: string) => keys.has(key), [keys]);

  const toggle = useCallback((key: string) => {
    setKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setAnchor(key);
  }, []);

  const addRange = useCallback((range: string[], a: string) => {
    setKeys((prev) => {
      const next = new Set(prev);
      for (const k of range) next.add(k);
      return next;
    });
    setAnchor(a);
  }, []);

  const setAll = useCallback((all: string[]) => {
    setKeys(new Set(all));
    setAnchor(all.length > 0 ? all[all.length - 1]! : null);
  }, []);

  const clear = useCallback(() => {
    setKeys(new Set());
    setAnchor(null);
  }, []);

  const resolve = useCallback(
    (items: MediaItem[]): MediaItem[] => items.filter((it) => keys.has(it.key)),
    [keys],
  );

  const keyList = useMemo(() => [...keys], [keys]);

  return useMemo(
    () => ({
      keys: keyList,
      active,
      anchor,
      begin,
      cancel,
      has,
      toggle,
      addRange,
      setAll,
      clear,
      resolve,
    }),
    [keyList, active, anchor, begin, cancel, has, toggle, addRange, setAll, clear, resolve],
  );
}
